
<template>
  <div class="home">
    <v-row justify="center">
      <v-col
        :xl="9"
        :sm="12"
        :xs="12"
      >
        <v-row>
          <v-col>
            <h1>Bike Collection / Service</h1>
            <p>Before we begin, we need to know a little bit about your Brompton and where it is being collected from or serviced at. This will help us give you our availability.</p>
          </v-col>
        </v-row>

        <v-form
          ref="form"
          v-model="validForm"
        >
          <v-row class="py-0">
            <v-col
              class="py-0"
              cols="12"
              sm="6"
            >
              <v-text-field
                :value="form.collectionPostcode"
                :disabled="collectionAddressData.addresses ? true : false"
                :rules="[v => !!v || 'Collection postcode is required']"
                label="Collection / Service Postcode"
                hint="The postcode where we should collect your bike from"
                persistent-hint
                outlined
                required
                dense
                @input="updateBookingFormValue($event, 'collectionPostcode')"
              />
            </v-col>

            <v-col
              class="py-0"
              cols="12"
              sm="6"
            >
              <v-text-field
                :value="form.serialNumber"
                :disabled="collectionAddressData.addresses ? true : false"
                :rules="[v => !!v || 'Serial number is required']"
                label="Serial Number"
                hint="The unique serial number on your brompton"
                persistent-hint
                outlined
                required
                dense
                @input="updateBookingFormValue($event, 'serialNumber')"
              />
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col>
              <v-btn
                v-if="!collectionAddressData.addresses"
                :loading="loading"
                dark
                large
                @click="checkAvailability('collectionPostcode', 'JOB_TYPE_DOORSTEP'); checkAvailability('collectionPostcode', 'JOB_TYPE_C&R')">
                Check Availability
              </v-btn>

              <v-btn
                v-else
                text
                small
                dense
                outlined
                color="primary"
                @click="doorstepCollectionAddressData = {} ; crCollectionAddressData = {}"
              >
                Choose a different postcode
              </v-btn>
            </v-col>
          </v-row>

          <template v-if="collectionPostcodeError.errors.postcode">
            <v-alert
              v-if="collectionPostcodeError.errors.postcode.length > 0"
              outlined
              border="top"
              type="error"
            >
              {{ collectionPostcodeError.errors.postcode[0] }}
            </v-alert>
          </template>

          <template v-if="collectionPostcodeError.errors.asset">
            <v-alert
              v-if="collectionPostcodeError.errors.asset.length > 0"
              outlined
              border="top"
              type="error"
            >
              The serial number provided is either not a valid Brompton Electric serial number, or this bicycle has already been booked for a firmware update elsewhere.
            </v-alert>
            <v-alert
              v-if="collectionPostcodeError.errors.asset.length > 0"
              outlined
              border="top"
              type="success"
            >
              <v-row align="center">
                <v-col class="grow">
                  If your Brompton has not been affected by the recall, but wish to book your bicycle in for a service with havebike, please click the link.
                </v-col>
                <v-col class="shrink">
                  <v-btn color="green" dark href="https://bookings.havebike.co.uk">Book Your Brompton Service</v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </template>

          <v-alert
            v-if="collectionAddressData.addresses"
            outlined
            border="top"
            type="success"
          >
            <strong>Great News!</strong> We have slots available for your {{ form.bikesForRepair === 1 ? 'bike' : 'bikes' }}! Please get started by choosing your collection address below.
          </v-alert>

          <h2 v-if="collectionAddressData.addresses">Service Option</h2>
          <p v-if="collectionAddressData.addresses" class="">To get started, please choose from one of the following service options below.</p>

          <v-radio-group
            v-if="collectionAddressData.addresses"
            v-model="service"
            dense
          >
            <v-row>
              <v-col
                v-for="method in availableServiceMethods"
                :key="method"
                cols="12"
                sm="4"
                class="service_content"
              >
                <v-radio
                  :label="method"
                  :value="method"
                >
                  <template v-slot:label>
                    <v-row class="py-0">
                      <v-col
                        cols="12"
                        sm="6"
                        class="service_content"
                      >
                        <div>
                          <h5 v-if="method === 'JOB_TYPE_DOORSTEP'">Free - Doorstep Service</h5>
                          <h5 v-else-if="method === 'JOB_TYPE_C&R_FIRMWARE'">Free - Collect & Return Firmware</h5>
                          <h5 v-else-if="method === 'JOB_TYPE_C&R'">Collect & Return Service</h5>

                          <p v-if="method === 'JOB_TYPE_DOORSTEP'">Our technician will carry out a 104 point safety check and undertake the firmware update at your doorstep</p>
                          <p v-else-if="method === 'JOB_TYPE_C&R_FIRMWARE'">We collect from you and return your Brompton 48-72 hrs later with the firmware update complete</p>
                          <p v-else-if="method === 'JOB_TYPE_C&R'">We collect from you and return your Brompton 48-72 hrs later with the firmware update and a <a href="https://www.havebike.co.uk/our-services/deluxe-service/" target="_blank">Deluxe service</a> complete</p>

                          <p v-if="method === 'JOB_TYPE_DOORSTEP' && availableDoorstepDays[0]" class="primary--text subtitle-2 font-weight-medium">Availability From: {{ $momentDate(availableDoorstepDays[0].date) }}</p>
                          <p v-else-if="availableCrDays[0] && method !== 'JOB_TYPE_DOORSTEP'" class="primary--text subtitle-2 font-weight-medium">Availability From: {{ $momentDate(availableCrDays[0].date) }}</p>

                          <p v-if="method === 'JOB_TYPE_DOORSTEP'"><strong>£5 Refundable Deposit</strong></p>
                          <p v-else-if="method === 'JOB_TYPE_C&R_FIRMWARE'"><strong>£5 Refundable Deposit</strong></p>
                          <p v-else-if="method === 'JOB_TYPE_C&R'"><strong>From <span style="text-decoration: line-through;">£89</span> <span style="text-decoration: underline;">£69.99</span></strong></p>
                        </div>
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <div v-if="method === 'JOB_TYPE_DOORSTEP'">
                          <img
                            class="most_popular"
                            src="https://d2w46cz0afxd34.cloudfront.net/assets/most-popular.png"
                          >
                          <img
                            src="https://d2w46cz0afxd34.cloudfront.net/assets/doorstep_service.jpg"
                            class="service_img"
                            height="150px"
                            width="100%"
                          >
                        </div>
                        <div v-if="method === 'JOB_TYPE_C&R_FIRMWARE'">
                          <img
                            src="https://d2w46cz0afxd34.cloudfront.net/assets/collect_and_return_service.jpg"
                            class="service_img"
                            height="150px"
                            width="100%"
                          >
                        </div>
                        <div v-if="method === 'JOB_TYPE_C&R'">
                          <img
                            class="most_popular"
                            src="https://d2w46cz0afxd34.cloudfront.net/assets/great_deal.png"
                          >
                          <img
                            src="https://d2w46cz0afxd34.cloudfront.net/assets/brompton_collect_and_return_service.jpg"
                            class="service_img"
                            height="150px"
                            width="100%"
                          >
                        </div>
                      </v-col>
                    </v-row>
                  </template>
                </v-radio>
              </v-col>
            </v-row>
          </v-radio-group>

          <v-divider v-if="collectionAddressData.addresses" class="mb-4" />

          <template v-if="collectionAddressData.addresses">
            <template v-if="collectionAddressData.collection_days.length > 0">
              <v-row>
                <v-col>
                  <h2 v-if="service === 'JOB_TYPE_DOORSTEP'">Service Address</h2>
                  <h2 v-else>Collection Address</h2>
                  <p v-if="service === 'JOB_TYPE_DOORSTEP'">Please choose the address we will service your bicycle at.</p>
                  <p v-else>Please choose the address we will be collecting from.</p>
                  <v-select
                    :value="form.collectionAddress"
                    :items="collectionAddressData.addresses"
                    label="Collection Address"
                    :rules="[v => !!v || 'Please choose a collection address']"
                    required
                    outlined
                    dense
                    @change="updateBookingFormValue($event, 'collectionAddress')"
                  />
                </v-col>
              </v-row>

              <h2 v-if="service === 'JOB_TYPE_DOORSTEP'">Service Date</h2>
              <h2 v-else>Collection Date</h2>
              <p>On what day would you like us to collect your {{ form.bikesForRepair === 1 ? 'bike' : 'bikes' }}?</p>

              <v-alert
                v-if="showLaterDateBanner"
                text
                dense
                border="top"
                color="primary"
              >
                <p class="mb-0 pb-0 mt-1">We're a bit busier than normal at the moment, the next available collection date is <strong>{{ firstWeekCollectionDays[0].date.format('ddd Do MMM') }}</strong></p>
              </v-alert>

              <v-radio-group
                v-if="!showFutureDates"
                :value="form.collectionDate"
                row
                dense
                @change="updateBookingFormValue($event, 'collectionDate')"
              >
                <v-radio
                  v-for="(day, key) in firstWeekCollectionDays"
                  :key="key"
                  :label="day.date.format('ddd Do MMM')"
                  :value="day.date.format('YYYY-MM-DD')"
                  :disabled="!day.available"
                />
              </v-radio-group>

              <v-dialog
                v-if="showFutureDates"
                ref="dialog"
                v-model="collectionDateModal"
                :return-value.sync="form.collectionDate"
                persistent
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="$store.getters.momentCollectionDate.format('ddd Do MMM')"
                    class="mt-4"
                    label="Collection / Service Date"
                    dense
                    outlined
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.collectionDate"
                  :allowed-dates="pickerAllowedCollectionDates"
                  :min="pickerMinCollectionDate"
                  scrollable
                >
                  <v-spacer />
                  <v-btn text color="primary" @click="collectionDateModal = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.dialog.save(form.collectionDate)">OK</v-btn>
                </v-date-picker>
              </v-dialog>

              <v-btn
                text
                small
                dense
                outlined
                color="primary"
                class="mb-3"
                @click="showFutureDates = !showFutureDates"
              >
                {{ showFutureDates ? 'Show Dates This Week?' : 'Show Later Dates?' }}
              </v-btn>

              <template v-if="service !== 'JOB_TYPE_DOORSTEP'">
                <v-divider class="mb-4" />

                <h2>Return Date</h2>
                <p>And on what day would you like us to return the {{ form.bikesForRepair === 1 ? 'bike' : 'bikes' }} to you?</p>
                <v-radio-group
                  :value="form.returnDate"
                  row
                  dense
                  @change="updateBookingFormValue($event, 'returnDate')"
                >
                  <v-radio
                    v-for="(day, key) in firstWeekReturnDays"
                    :key="key"
                    :label="day.date.format('ddd Do MMM')"
                    :value="day.date.format('YYYY-MM-DD')"
                    :disabled="!day.available"
                  />
                </v-radio-group>

                <v-btn
                  v-if="!form.alternateReturnPostcode"
                  text
                  small
                  dense
                  outlined
                  color="primary"
                  @click="updateBookingFormValue(true, 'alternateReturnPostcode')"
                >
                  Return to a different address?
                </v-btn>

                <v-btn
                  v-else
                  text
                  small
                  dense
                  outlined
                  color="primary"
                  @click="updateBookingFormValue(false, 'alternateReturnPostcode');
                  updateBookingFormValue(null, 'returnAddress');
                  updateBookingFormValue(null, 'returnPostcode');
                  returnAddressData = {}"
                >
                  Return to the collection address?
                </v-btn>

                <template v-if="form.alternateReturnPostcode">
                  <h2 class="mt-2">Return Address</h2>
                  <p>If you would like us to return the {{ form.bikesForRepair === 1 ? 'bike' : 'bikes' }} to a different address, please provide that below.</p>
                  <v-row>
                    <v-col
                      class="py-0"
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        :disabled="returnAddressData.addresses ? true : false"
                        :value="form.returnPostcode"
                        label="Return Postcode"
                        :rules="[v => !!v || 'Please choose a enter a return postcode']"
                        required
                        outlined
                        dense
                        @input="updateBookingFormValue($event, 'returnPostcode')"/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="pt-0">
                      <v-btn
                        v-if="returnAddressData.addresses ? false : true"
                        :loading="loading"
                        color="primary"
                        @click="checkAvailability('returnPostcode')">
                        Check Return Availability
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col class="py-0">
                      <v-select
                        v-if="returnAddressData.addresses"
                        :value="form.returnAddress"
                        :items="returnAddressData.addresses"
                        :rules="[v => !!v || 'Please choose a return address']"
                        required
                        label="Return Address"
                        outlined
                        dense
                        @change="updateBookingFormValue($event, 'returnAddress')"
                      />
                    </v-col>
                  </v-row>
                </template>
              </template>

              <v-row class="pt-0">
                <v-col class="pt-0">
                  <v-btn
                    color="primary"
                    class="mt-4"
                    large
                    @click="validateAndNext">
                    Add Your Bikes <v-icon right dark>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              <v-alert
                outlined
                border="top"
                type="error"
              >
                Sorry we don't currently have any availability in {{ collectionAddressData.postcode_data.nuts }}. Please call us on 020 3397 0888
              </v-alert>
            </template>
          </template>
        </v-form>
      </v-col>
    </v-row>

    <v-dialog
      v-model="discountDialog"
      width="500"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">{{ discountDialogTitle }}</v-card-title>
        <v-card-text class="pt-4" v-html="discountDialogMessage" />
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="discountDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formPage from '@/views/formPage'
import moment from 'moment'
import filter from 'lodash.filter'
export default {
  name: 'pageOne',
  mixins: [formPage],
  data () {
    return {
      loading: false,
      collectionPostcodeError: {
        errors: {
          postcode: []
        }
      },
      returnPostcodeError: {
        errors: {
          postcode: []
        }
      },
      service: 'JOB_TYPE_C&R_FIRMWARE',
      crCollectionAddressData: {},
      doorstepCollectionAddressData: {},
      returnAddressData: {},
      validForm: true,
      collectionDateModal: false,
      showFutureDates: false,
      discountDialog: false,
      discountDialogTitle: null,
      discountDialogMessage: null,
      assetId: null
    }
  },
  mounted () {
    this.trackCheckoutStep1()
    if (this.$route.query.postcode) {
      this.updateBookingFormValue(this.$route.query.postcode, 'collectionPostcode')
    }
    if (this.$route.query.service) {
      this.$store.commit('setDefaultService', this.$route.query.service)
    }
  },
  watch: {
    'form.collectionDate': function (value) {
      var dates = filter(this.firstWeekReturnDays, day => {
        return day.available
      })
      if (dates[0]) {
        this.updateBookingFormValue(dates[0].date.format('YYYY-MM-DD'), 'returnDate')
      }
    }
  },
  computed: {
    availableServiceMethods () {
      if (this.availableDoorstepDays[0]) {
        return ['JOB_TYPE_DOORSTEP', 'JOB_TYPE_C&R', 'JOB_TYPE_C&R_FIRMWARE']
      }
      return ['JOB_TYPE_C&R', 'JOB_TYPE_C&R_FIRMWARE']
    },

    collectionAddressData () {
      if (this.service === 'JOB_TYPE_DOORSTEP') {
        return this.doorstepCollectionAddressData
      }
      return this.crCollectionAddressData
    },

    showLaterDateBanner () {
      if (this.firstWeekCollectionDays[0]) {
        return moment().add(1, 'day').format('YYYY-MM-DD') !== this.firstWeekCollectionDays[0].date.format('YYYY-MM-DD')
      }
      return false
    },

    pickerMinCollectionDate () {
      return moment().add(1, 'day').format('YYYY-MM-DD')
    },

    availableDoorstepDays () {
      var days = []
      if (this.doorstepCollectionAddressData.collection_days) {
        this.doorstepCollectionAddressData.collection_days.forEach(day => {
          if (day.available) {
            days.push({ ...day, ...{ date: moment(day.date) } })
          }
        })
      }
      return days
    },

    availableCrDays () {
      var days = []
      if (this.crCollectionAddressData.collection_days) {
        this.crCollectionAddressData.collection_days.forEach(day => {
          if (day.available) {
            days.push({ ...day, ...{ date: moment(day.date) } })
          }
        })
      }
      return days
    },

    firstWeekCollectionDays () {
      var days = []
      this.collectionAddressData.collection_days.forEach(day => {
        if (day.available) {
          days.push({ ...day, ...{ date: moment(day.date) } })
        }
      })
      return days.slice(0, 10)
    },
    firstWeekReturnDays () {
      var days = []
      this.collectionAddressData.return_days.forEach((day) => {
        var momentDate = moment(day.date)
        if (moment(this.form.collectionDate).add(2, 'day') < momentDate) {
          days.push({ ...day, ...{ date: momentDate } })
        }
      })
      return days.slice(0, 7)
    }
  },
  methods: {
    trackCheckoutStep1 () {
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: { step: 1 }
          }
        }
      })
    },
    trackCheckoutStep2 () {
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: { step: 2, option: `${this.form.bikesForRepair} bikes` }
          }
        }
      })
    },
    pickerAllowedCollectionDates (val) {
      var date = moment(val).format('YYYY-MM-DD')
      var dates = filter(this.collectionAddressData.collection_days, day => {
        return moment(day.date).format('YYYY-MM-DD') === date
      })
      if (dates[0]) {
        return dates[0].available
      }
      return false
    },
    checkAvailability (type, method) {
      var isCollection = type === 'collectionPostcode'
      var postcode = isCollection ? this.form.collectionPostcode : this.form.returnPostcode
      this.loading = true
      this.collectionPostcodeError = {
        errors: {
          postcode: []
        }
      }
      this.$api.persist('post', {
        path: 'availability',
        object: {
          postcode: postcode,
          number_of_bikes: this.form.bikesForRepair,
          asset: this.form.serialNumber || 'xxx',
          types: [method]
        }
      })
        .then(({ data }) => {
          if (isCollection) {
            if (data.asset_data) {
              this.assetId = data.asset_data.id
            }
            if (method === 'JOB_TYPE_DOORSTEP') {
              this.doorstepCollectionAddressData = {
                postcode_data: data.postcode_data,
                collection_days: data.collection_days,
                return_days: data.return_days,
                addresses: data.addresses
              }
              if (this.availableDoorstepDays[0]) {
                this.service = 'JOB_TYPE_DOORSTEP'
              }
              if (this.doorstepCollectionAddressData.collection_days.length > 0 && this.doorstepCollectionAddressData.return_days.length > 0 && !this.form.collectionDate) {
                this.trackCheckoutStep2()
                var availableCollectionDates = filter(this.doorstepCollectionAddressData.collection_days, day => {
                  return day.available === true
                })
                if (availableCollectionDates[0]) {
                  this.updateBookingFormValue(moment(availableCollectionDates[0].date).format('YYYY-MM-DD'), 'collectionDate')
                }
              }
            } else {
              this.crCollectionAddressData = {
                postcode_data: data.postcode_data,
                collection_days: data.collection_days,
                return_days: data.return_days,
                addresses: data.addresses
              }
            }
            this.checkForAutoDiscount(data.postcode_data)
          } else {
            this.returnAddressData = {
              postcode_data: data.postcode_data,
              collection_days: data.collection_days,
              return_days: data.return_days,
              addresses: data.addresses
            }
            this.returnPostcodeError = {
              errors: {
                postcode: []
              }
            }
          }
        })
        .catch(error => {
          if (isCollection) {
            if (method === 'JOB_TYPE_DOORSTEP') {
              this.doorstepCollectionAddressData = {}
            } else {
              this.crCollectionAddressData = {}
            }
            this.collectionPostcodeError = error
          } else {
            this.returnAddressData = {}
            this.returnPostcodeError = error
          }
        })
        .finally(() => { this.loading = false })
    },

    checkForAutoDiscount (postcodeData) {
      // City of London Discount Message
      if (postcodeData.admin_district === 'City of London') {
        // this.discountDialog = true
        // this.discountDialogTitle = 'City of London Discount'
        // this.discountDialogMessage = 'havebike are working with the City of London Corporation to offer all residents 20% off our Regular or Deluxe service. Please use discount code <strong>CITY20COMMUNITY</strong> at checkout!'
      }
    },

    validateAndNext () {
      if (this.$refs.form.validate()) {
        if (this.service === 'JOB_TYPE_DOORSTEP' || this.service === 'JOB_TYPE_C&R_FIRMWARE') {
          this.$store.dispatch('setupBikes', {
            sku: 'brompton_firmware',
            spend: 0,
            asset: this.assetId,
            job_type: this.service === 'JOB_TYPE_DOORSTEP' ? 'JOB_TYPE_DOORSTEP' : 'JOB_TYPE_C&R'
          })
        }
        if (this.service === 'JOB_TYPE_C&R') {
          this.$store.dispatch('setupBikes', {
            sku: 'brompton_deluxe_firmware',
            spend: 20,
            asset: this.assetId,
            job_type: 'JOB_TYPE_C&R'
          })
        }
        this.$router.push({ name: 'pageTwo' })
      }
    }
  }
}
</script>
<style type="text/css" scoped>
  .most_popular {
    position: absolute;
    right: 0;
    width: 150px;
  }
  .service_content {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .service_img {
    object-fit: cover;
  }
</style>
